/**
 * GroupAssignmentSelect
 *
 * Compact dual-select for assigning an agent to a group + (optional) subgroup
 * within that group. Used in `<AgentCard>` whenever the admin has at least
 * one group defined.
 *
 *   [ Group ▾ ]  [ Subgroup ▾ ]     (Subgroup disabled until Group is set)
 *
 * "(Sin grupo)" is a sentinel that maps to `groupId = null` (clears the
 * assignment for the calling admin).
 *
 * If the agent is in a subgroup, the Subgroup select is enabled and
 * pre-populated with that subgroup. If only in a group, the Subgroup select
 * stays disabled (it only lists subgroups belonging to the selected group).
 */

import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useMemo } from 'react';
import type { Agent } from '@/types/agent';
import type { Subgroup, UserGroup } from '@/types/groups';
import { useUpdateAgentAssignment } from '@/hooks/useGroups';

const SIN_GRUPO = '__none__';

interface GroupAssignmentSelectProps {
  agent: Agent;
  groups: UserGroup[];
}

export const GroupAssignmentSelect: React.FC<GroupAssignmentSelectProps> = ({ agent, groups }) => {
  const updateAssignment = useUpdateAgentAssignment();

  // Selected group: prefer current agent.groupId, fallback to first group.
  const selectedGroupId = agent.groupId ?? '';

  // Subgroups of the currently selected group (or empty if no group).
  const availableSubgroups: Subgroup[] = useMemo(() => {
    if (!selectedGroupId) return [];
    const g = groups.find((gg) => gg.id === selectedGroupId);
    return g?.subgroups ?? [];
  }, [groups, selectedGroupId]);

  const handleGroupChange = (e: SelectChangeEvent<string>) => {
    const v = e.target.value;
    if (v === SIN_GRUPO) {
      // Clear assignment entirely (group_id + subgroup_id null).
      updateAssignment.mutate({ agentId: agent.agentId, body: {} });
      return;
    }
    // Switching to a different group: clear subgroup unless it still belongs.
    const newGroup = groups.find((g) => g.id === v);
    const stillValid =
      agent.subgroupId && newGroup?.subgroups.some((s) => s.id === agent.subgroupId);
    updateAssignment.mutate({
      agentId: agent.agentId,
      body: { groupId: v, subgroupId: stillValid ? agent.subgroupId : null },
    });
  };

  const handleSubgroupChange = (e: SelectChangeEvent<string>) => {
    const v = e.target.value;
    if (v === SIN_GRUPO) {
      updateAssignment.mutate({
        agentId: agent.agentId,
        body: { groupId: selectedGroupId, subgroupId: null },
      });
      return;
    }
    updateAssignment.mutate({
      agentId: agent.agentId,
      body: { groupId: selectedGroupId, subgroupId: v },
    });
  };

  if (groups.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
      data-testid={`agent-assignment-select-${agent.agentId}`}
    >
      <FormControl
        size="small"
        sx={{ minWidth: 140 }}
        data-testid={`agent-group-wrap-${agent.agentId}`}
      >
        <InputLabel id={`group-lbl-${agent.agentId}`}>Grupo</InputLabel>
        <Select
          labelId={`group-lbl-${agent.agentId}`}
          label="Grupo"
          value={selectedGroupId || SIN_GRUPO}
          onChange={handleGroupChange}
        >
          <MenuItem value={SIN_GRUPO} data-testid={`agent-group-option-none-${agent.agentId}`}>
            <em>(Sin grupo)</em>
          </MenuItem>
          {groups.map((g) => (
            <MenuItem
              key={g.id}
              value={g.id}
              data-testid={`agent-group-option-${agent.agentId}-${g.id}`}
            >
              {g.color && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: g.color,
                    mr: 1,
                    verticalAlign: 'middle',
                  }}
                />
              )}
              {g.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl
        size="small"
        sx={{ minWidth: 140 }}
        disabled={!selectedGroupId || availableSubgroups.length === 0}
        data-testid={`agent-subgroup-wrap-${agent.agentId}`}
      >
        <InputLabel id={`subgroup-lbl-${agent.agentId}`}>Subgrupo</InputLabel>
        <Select
          labelId={`subgroup-lbl-${agent.agentId}`}
          label="Subgrupo"
          value={agent.subgroupId ?? SIN_GRUPO}
          onChange={handleSubgroupChange}
        >
          <MenuItem value={SIN_GRUPO} data-testid={`agent-subgroup-option-none-${agent.agentId}`}>
            <em>(Sin subgrupo)</em>
          </MenuItem>
          {availableSubgroups.map((s) => (
            <MenuItem
              key={s.id}
              value={s.id}
              data-testid={`agent-subgroup-option-${agent.agentId}-${s.id}`}
            >
              {s.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {updateAssignment.isError && (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'center' }}>
          Error al guardar
        </Typography>
      )}
    </Box>
  );
};
